import { useEffect, useState } from "react";
import { Plus, Send, Trash2, User, Copy } from "lucide-react";
import { addTrust, listTrust, removeTrust } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function TrustCircle() {
  const { uid } = useApp();
  const { user } = useAuth();
  const effectiveId = user?.user_id || uid;
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ label: "Friend", name: "", phone: "", relation: "" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const list = await listTrust(effectiveId);
      setContacts(list);
    } catch (e) {
      /* noop */
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId]);

  const add = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await addTrust(effectiveId, form);
      setForm({ label: "Friend", name: "", phone: "", relation: "" });
      await refresh();
      toast.success("Contact added to Trust Circle");
    } catch (e) {
      toast.error("Could not add contact");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await removeTrust(effectiveId, id);
      setContacts((c) => c.filter((x) => x.id !== id));
    } catch (e) {
      /* noop */
    }
  };

  const sendStatus = async (c) => {
    const msg = `LIFEGRID STATUS\nHi ${c.name}, I wanted to share my current status via LIFEGRID.\nSituation: Requesting attention\nTime: ${new Date().toLocaleString()}\n— demo message`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Status for ${c.name}`, text: msg });
        return;
      } catch (e) {
        /* cancelled */
      }
    }
    await navigator.clipboard.writeText(msg);
    toast.success(`Status message for ${c.name} copied`);
  };

  return (
    <section id="trust" className="relative py-20 md:py-28 border-t border-white/5">
      <div className="absolute inset-0 tac-grid-fine opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 08 // TRUST CIRCLE</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-3xl">
          The people you can <span className="text-lime-accent">reach in one tap</span>.
        </h2>

        <div className="mt-10 grid md:grid-cols-5 gap-6">
          <div className="md:col-span-2 glass rounded-md p-5">
            <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">ADD CONTACT</div>
            <div className="mt-4 space-y-3">
              <select
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
                data-testid="trust-label"
              >
                {["Mom", "Dad", "Partner", "Friend", "Teacher", "Neighbour", "Doctor", "Other"].map((l) => (
                  <option key={l} value={l} className="bg-[color:var(--lg-void)]">
                    {l}
                  </option>
                ))}
              </select>
              <input
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
                data-testid="trust-name"
              />
              <input
                placeholder="Phone or handle"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
                data-testid="trust-phone"
              />
              <input
                placeholder="Relation (optional)"
                value={form.relation}
                onChange={(e) => setForm({ ...form, relation: e.target.value })}
                className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
                data-testid="trust-relation"
              />
              <button className="btn-tactile primary w-full" onClick={add} disabled={busy || !form.name.trim()} data-testid="trust-add">
                <Plus size={14} /> Add to Trust Circle
              </button>
            </div>
          </div>

          <div className="md:col-span-3">
            {contacts.length === 0 ? (
              <div className="glass rounded-md p-6 text-center text-[color:var(--lg-text-2)]" data-testid="trust-empty">
                Your Trust Circle is empty. Add the people you would want to know if something happened.
              </div>
            ) : (
              <ul className="space-y-3">
                {contacts.map((c) => (
                  <li key={c.id} className="glass rounded-md p-4 flex items-center gap-4" data-testid={`trust-item-${c.id}`}>
                    <div className="h-11 w-11 rounded-sm border border-white/15 grid place-items-center text-[color:var(--lg-cyan)]">
                      <User size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold uppercase">{c.name}</span>
                        <span className="font-mono-tac text-[10px] tracking-[0.24em] text-[color:var(--lg-lime)]">
                          {c.label}
                        </span>
                      </div>
                      <div className="text-xs text-[color:var(--lg-text-3)] truncate">
                        {[c.relation, c.phone].filter(Boolean).join(" · ") || "No details"}
                      </div>
                    </div>
                    <button className="btn-tactile primary !py-2 !px-3" onClick={() => sendStatus(c)} data-testid={`trust-send-${c.id}`}>
                      <Send size={14} /> Send Status
                    </button>
                    <button className="btn-tactile ghost !py-2 !px-3" onClick={() => remove(c.id)} data-testid={`trust-remove-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-4 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
              DEMO — LIFEGRID DOES NOT ACTUALLY SEND SMS. "SEND STATUS" USES THE WEB SHARE API OR COPY TO CLIPBOARD.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
